/**
 * Mini games at the golden bottle (user 2026-10-04): instead of always the Bau-Quiz a random one of quiz, crane,
 * hammer & nail and concrete mixer. The picture freezes as for the quiz; the bonus is the quiz round's bonus
 * (QuizConfig.rounds by run distance) times the share the player reached (0 … 1). 100 % has to be really hard
 * ("es muss wirklich schwer sein") – the perfect windows are a few hundredths of a second wide.
 *
 * Difficulty per quiz round index (0: from 0 m, 1: from 2000 m, 2: from 4000 m) – every list has three entries.
 */
export interface HammerConfig {
  /** Strikes per nail (user: "max. 3 Schläge"). */
  readonly strikes: number;
  /** Full back-and-forth of the hammer over the nail (s) per round – sinusoidal, fastest over the nail. */
  readonly periods: readonly [number, number, number];
  /** Each strike makes the swing faster by this factor. */
  readonly speedUpPerStrike: number;
  /** Half width of the perfect hit (swing units): the nail goes in completely ("mittig … geht er rein"). */
  readonly perfectHalfWidth: number;
  /** Half width of a hit at all (hammer face still on the nail head): a side hit drives it 10 %. */
  readonly hitHalfWidth: number;
  /** How far a side hit drives the nail (user: "nur zu 10 % rein"). */
  readonly sideDrive: number;
  /** Seconds per strike before it counts as missed. */
  readonly secondsPerStrike: number;
  /** Strike animation: hammer down, impact, back up (s); taps are ignored meanwhile. */
  readonly strikeSeconds: number;
}

export interface CraneConfig {
  /** Loads per round. */
  readonly loads: number;
  /**
   * The load swings calmly on the rope under a standing trolley (user 2026-10-04: "langsam pendeln … maximal zehn bis
   * zwanzig Grad von der Vertikalen" – the trolley racing along the jib was far too hectic): swing amplitude per round
   * (degrees from the vertical).
   */
  readonly swingDegrees: readonly [number, number, number];
  /** Rope length (m) – a pendulum of 5 m swings once in about 4.5 s. */
  readonly ropeLength: number;
  /**
   * Where the mark lies relative to the point under the trolley, as a share of the reach of a release (the load flies
   * on with its swing): from `min` to `max`, left or right at random – never straight under the trolley.
   */
  readonly targetOffset: { readonly min: number; readonly max: number };
  /** Drop height from the hook to the slab (m). */
  readonly dropHeight: number;
  /** Gravity of the drop (m/s², a little more than real so the load does not float). */
  readonly gravity: number;
  /** Half width of the marked target on the slab (m). */
  readonly targetHalfWidth: number;
  /** Half width of a perfect set-down (m). */
  readonly perfectHalfWidth: number;
  /** Share of a load set down on the marker but off centre: from `edge` (at the marker edge) to `near` (just outside perfect). */
  readonly onTarget: { readonly near: number; readonly edge: number };
  /** Seconds per load before it counts as missed (about two swings). */
  readonly secondsPerLoad: number;
  /** Pause after a landing before the next load hangs on the hook (s). */
  readonly settleSeconds: number;
}
export interface MixerConfig {
  /** Ingredients, one stop of the needle each. */
  readonly ingredients: readonly string[];
  /** Seconds the needle needs for one sweep over the dial (constant speed), per round – each stop is faster. */
  readonly sweepSeconds: readonly [number, number, number];
  readonly speedUpPerStop: number;
  /** Dial range (degrees, needle from −range/2 to +range/2). */
  readonly dialDegrees: number;
  /** Half width of the green zone (degrees) per stop – it narrows. */
  readonly zoneHalfWidths: readonly number[];
  /** Half width of the perfect line in the middle of the zone (degrees). */
  readonly perfectHalfWidth: number;
  /** Share of a stop in the green zone but off the line: from `edge` to `near`. */
  readonly inZone: { readonly near: number; readonly edge: number };
  readonly secondsPerStop: number;
  /** Pause after a stop (pouring) before the next needle runs (s). */
  readonly pourSeconds: number;
}

export interface MiniGamesConfig {
  /** Mini games in the random draw (the quiz joins when its question pool is loaded). */
  readonly enabled: boolean;
  /** Seconds the result stays before the run goes on. */
  readonly resultSeconds: number;
  /** Keys that strike / drop / stop (besides a tap anywhere). */
  readonly actionKeys: readonly string[];
  readonly hammer: HammerConfig;
  readonly crane: CraneConfig;
  readonly mixer: MixerConfig;
}

export const MINIGAMES_CONFIG: MiniGamesConfig = {
  // on since 2026-10-04: hammer & nail in 3D (Blender set PROP_MG_HAMMER_SET), crane and mixer as 2D scenes
  enabled: true,
  resultSeconds: 2.4,
  actionKeys: [' ', 'Enter', 'ArrowUp', 'ArrowDown', 'w', 's'],
  hammer: {
    strikes: 3,
    // over the nail the head moves at 2π/period swing units per second: at 1.5 s the perfect window is ±21 ms
    periods: [1.5, 1.3, 1.15],
    speedUpPerStrike: 1.1,
    perfectHalfWidth: 0.09,
    hitHalfWidth: 0.34,
    sideDrive: 0.1,
    secondsPerStrike: 4,
    strikeSeconds: 0.5,
  },
  crane: {
    loads: 3,
    swingDegrees: [12, 16, 20],
    ropeLength: 5,
    targetOffset: { min: 0.4, max: 0.8 },
    dropHeight: 6,
    gravity: 18,
    targetHalfWidth: 1.1,
    perfectHalfWidth: 0.12,
    onTarget: { near: 0.45, edge: 0.1 },
    secondsPerLoad: 10,
    settleSeconds: 1,
  },  mixer: {
    ingredients: ['Wasser', 'Zement', 'Kies'],
    // constant needle speed: 180° in 1.1 s = 164°/s – the perfect line (±2.5°) is a window of ±15 ms
    sweepSeconds: [1.1, 0.95, 0.85],
    speedUpPerStop: 1.08,
    dialDegrees: 180,
    zoneHalfWidths: [10, 7.5, 5.5],
    perfectHalfWidth: 2.5,
    inZone: { near: 0.55, edge: 0.15 },
    secondsPerStop: 4,
    pourSeconds: 0.9,
  },
};
